import { styled } from '@linaria/react';

import useSound from '@/hooks/use-sound';

import VisuallyHidden from '@/components/VisuallyHidden';

function FlippableValue({
  unit,
  imperial,
  metric,
  imperialWidth,
  metricWidth,
  handleToggle,
}: {
  unit: 'metric' | 'imperial';
  imperial: string;
  metric: string;
  imperialWidth: number;
  metricWidth: number;
  handleToggle?: () => void;
}) {
  const longTransition = 300;
  const shortTransition = 500;

  const longerUnit =
    imperialWidth > metricWidth ? 'imperial' : 'metric';

  const [play] = useSound('/sounds/empty-trash.mp3', {
    volume: 0.5,
  });

  return (
    <Wrapper
      style={{
        width:
          unit === 'imperial'
            ? imperialWidth / 16 + 'rem'
            : metricWidth / 16 + 'rem',
        '--transition': `width ${longerUnit === unit ? longTransition : shortTransition}ms`,
      }}
      onClick={() => {
        play();
        handleToggle?.();
      }}
    >
      <Value
        aria-hidden={unit === 'metric' ? 'true' : 'false'}
        style={{
          transform: `rotateX(${unit === 'imperial' ? 0 : 180}deg)`,
          '--transition': `transform ${longerUnit === unit ? shortTransition : longTransition}ms`,
        }}
      >
        {imperial}
      </Value>
      <Value
        aria-hidden={unit === 'imperial' ? 'true' : 'false'}
        style={{
          transform: `rotateX(${unit === 'metric' ? 0 : 180}deg)`,
          '--transition': `transform ${longerUnit === unit ? shortTransition : longTransition}ms`,
        }}
      >
        {metric}
      </Value>
      <VisuallyHidden>
        {'. '}
        Triggering this button toggles between imperial and metric
        units.
      </VisuallyHidden>
    </Wrapper>
  );
}

const Wrapper = styled.button`
  position: relative;
  display: inline grid;
  -webkit-tap-highlight-color: transparent;

  @media (prefers-reduced-motion: no-preference) {
    transition: var(--transition);
  }

  /*
    Manage with 'focus-within' in parent.
    This is to prevent a funky glitched focus outline, since the width changes.
  */
  @supports selector(:has(*)) {
    &:focus-visible {
      outline: none;
    }
  }
`;

const Value = styled.span`
  backface-visibility: hidden;
  grid-row: 1;
  grid-column: 1;

  @media (prefers-reduced-motion: no-preference) {
    transition: var(--transition);
  }
`;

export default FlippableValue;
